import i18n from 'i18next';
import moment from 'moment';
import {EntitySocialResponse} from '../../backend/interfaces/entity-social-response';
import {User} from '../../backend/interfaces/user';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {
  EntityOccurrence,
  EntityOccurrenceAcknowledgement,
  EntityOccurrenceHistory,
  EntityOccurrenceType,
} from '../../backend/responses/entity-occurrence';
import {ScreenPaths} from '../../enums';
import {UsersRepository} from '../../repositories/users';
import type {OccurrenceStatus} from '../../screens/private/office/professional-caregiver/occurrences/occurrences.professional-caregiver.screen';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {Attachment} from '../../backend/interfaces/attachment';

export type TransformedOccurrence = {
  id: string;
  registeredAt: Date;
  typeId: string;
  type: string;
  patient?: {
    id: string;
    name: string;
    picture?: string;
    socialResponses: EntitySocialResponse[];
  };
  responsible?: {
    id: string;
    name: string;
    picture?: string;
  };
  registeredBy?: {
    id: string;
    name: string;
    picture?: string;
  };
  status: OccurrenceStatus;
  local?: string;
  details: string;
  notes?: string;
  actionRequired?: boolean;
  markedAsHandled?: boolean;
  acknowledgements: EntityOccurrenceAcknowledgement[];
  history?: EntityOccurrenceHistory;
  attachments?: Attachment[];
};

export type HandleOccurrenceStep =
  | 'acknowledge'
  | 'handle'
  | 'insert-notes-only'
  | 'handled-edit'
  | 'unknown';

export function getOccurrenceStatus(occurrence: EntityOccurrence): OccurrenceStatus {
  return occurrence.actionRequired && occurrence.markedAsHandled
    ? 'handled'
    : occurrence.actionRequired && !occurrence.markedAsHandled
      ? 'not_handled'
      : 'registered';
}

export function sortOccurrenceTypesByName(
  occurrenceTypes: EntityOccurrenceType[]
): EntityOccurrenceType[] {
  const locale = (i18n.resolvedLanguage || i18n.language)?.replaceAll('_', '-');

  return [...occurrenceTypes].sort((a, b) =>
    a.occurrenceType.name.localeCompare(b.occurrenceType.name, locale, {sensitivity: 'base'})
  );
}

export async function transformOccurrences(
  occurrences: EntityOccurrence[],
  types: EntityOccurrenceType[],
  seniors?: AffiliatedUser[],
  professionals?: AffiliatedUser[]
): Promise<TransformedOccurrence[]> {
  const otherProfessionals = await getOtherProfessionals(occurrences, professionals);

  const result = occurrences.reduce<TransformedOccurrence[]>((acc, occurrence) => {
    const occurrenceType = types.find((type) => type.occurrenceType.id === occurrence.typeId);
    const user = seniors?.find((user) => user.user.id === occurrence.targetUserId);
    let responsible = professionals?.find(
      (user) => user.user.id === occurrence.responsibleUserId
    )?.user;
    let registeredBy = professionals?.find(
      (user) => user.user.id === occurrence.registeredByUserId
    )?.user;

    // if user not found, skip this occurrence
    // ################################################
    // HACK: Backend should return only active users
    // TODO: Remove this hack when backend is fixed
    // ################################################
    if (!user && occurrenceType?.occurrenceType.category === 'patients') {
      return acc;
    }

    const other = (otherProfessionals?.filter((other) => other !== undefined) as User[]) || [];

    if (occurrence.responsibleUserId && !responsible) {
      const otherProfessional = other.find((user) => user.id === occurrence.responsibleUserId);
      responsible = otherProfessional;
    }
    if (!registeredBy) {
      const otherProfessional = other.find((user) => user.id === occurrence.registeredByUserId);
      registeredBy = otherProfessional;
    }

    const ocurr: TransformedOccurrence = {
      id: occurrence.id,
      registeredAt: occurrence.registeredAt,
      typeId: occurrenceType?.occurrenceType.id || '',
      type: occurrenceType?.occurrenceType.name || '-',
      patient: user
        ? {
            id: user.user.id || '',
            name: user.user.displayName || user.user.name || '',
            picture: user.user.picture,
            socialResponses: user.entitySocialResponses || [],
          }
        : undefined,
      responsible: responsible
        ? {
            id: responsible.id || '',
            name: responsible.displayName || responsible.name || '',
            picture: responsible.picture,
          }
        : undefined,
      registeredBy: registeredBy
        ? {
            id: registeredBy.id || '',
            name: registeredBy.displayName || registeredBy.name || '',
            picture: registeredBy.picture,
          }
        : undefined,
      status: getOccurrenceStatus(occurrence),
      local: occurrence.siteName,
      details: occurrence.details,
      notes: occurrence.notes,
      acknowledgements: occurrence.acknowledgements || [],
      actionRequired: occurrence.actionRequired,
      markedAsHandled: occurrence.markedAsHandled,
      history: occurrence.history,
      attachments: occurrence.attachments,
    };

    acc.push(ocurr);

    return acc;
  }, []);

  return result;
}

export function getHandleOccurrenceStep(
  occurrence: TransformedOccurrence,
  userId?: string,
  acknowledging: boolean = false
): HandleOccurrenceStep {
  // https://sioslife.atlassian.net/wiki/spaces/SE/pages/2244149252/CW+DCM -> Occurrences documentation
  const acknowledge = occurrence.acknowledgements.find((ack) => ack.userAuthorId === userId);
  const userAllowedToHandle =
    (occurrence.responsible && occurrence.responsible.id === userId) ||
    (occurrence.registeredBy && occurrence.registeredBy.id === userId) ||
    acknowledge
      ? true
      : false;

  if (
    !acknowledge ||
    (acknowledge?.acknowledgedAt && !occurrence.actionRequired) ||
    (!occurrence.actionRequired && acknowledging)
  ) {
    // if user is not on the acknowledgements
    // if the user already give acknowledgement and the occurrence is not action required
    // if the user has just acknowledged the occurrence but the occurrence is not action required
    // if the user is not allowed to handle the occurrence
    return userAllowedToHandle ? 'handle' : 'insert-notes-only';
  } else if (acknowledge && !acknowledge.acknowledgedAt && !acknowledging) {
    // if user is on the acknowledgements but not yet give acknowledgement
    return 'acknowledge';
  } else if (occurrence.actionRequired) {
    // if the occurrence is action required and the user already give acknowledgement

    // check if user has permission to handle the occurrence
    if (!userAllowedToHandle) {
      return 'insert-notes-only';
    } else {
      if (occurrence.markedAsHandled === undefined) {
        return 'handle';
      } else {
        return 'handled-edit';
      }
    }
  }

  console.warn('getHandleOccurrenceStep: unknown step');
  // none of the above conditions! I don't know what to do
  return 'unknown';
}

async function getOtherProfessionals(
  occurrences: EntityOccurrence[],
  professionals?: AffiliatedUser[]
): Promise<User[]> {
  const professionalsIds = professionals?.map((item) => item.user.id || '') || [];
  const promises: Promise<User | undefined>[] = [];

  occurrences.forEach(async (occurrence) => {
    if (!professionalsIds?.includes(occurrence.registeredByUserId)) {
      promises.push(UsersRepository.instance.getById(occurrence.registeredByUserId));
    }
    if (occurrence.responsibleUserId && !professionalsIds?.includes(occurrence.responsibleUserId)) {
      promises.push(UsersRepository.instance.getById(occurrence.responsibleUserId));
    }
  });
  const results = await Promise.all(promises);

  return results.filter((user): user is User => user !== undefined);
}

export function getOccurrencePath(
  from: 'office' | 'daily-care-log',
  entityId: string,
  occurrenceId: string,
  registeredAt: string,
  hadReadPermission: (permission: RolePermissionType) => boolean,
  appAlreadyOpened: boolean
): string {
  let occurrencePath = '';

  if (appAlreadyOpened) {
    // site was already opened when the notification was clicked
    // check if it was daily care or office that was already opened
    if (from === 'office') {
      if (hadReadPermission(RolePermissionType.Occurrences)) {
        occurrencePath = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Occurrences}?occurrenceId=${occurrenceId}&occurrenceDate=${moment(registeredAt).format('YYYY-MM-DD')}`;
      } else {
        console.warn('no permissions to office occurrences');
        occurrencePath = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}`;
      }
    } else if (from === 'daily-care-log') {
      if (hadReadPermission(RolePermissionType.DailyCareLog_Occurrences)) {
        occurrencePath = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.ProfessionalCaregiver_Occurrences}/${occurrenceId}?date=${moment(registeredAt).format('YYYY-MM-DD')}`;
      } else {
        console.warn('no permissions to daily care log occurrences');
        occurrencePath = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Home}`;
      }
    }
  } else {
    // site was not opened when the notification was clicked
    // try to open occurrence on daily care log by default if its mobile
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    if (isMobile) {
      if (hadReadPermission(RolePermissionType.DailyCareLog_Occurrences)) {
        occurrencePath = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.ProfessionalCaregiver_Occurrences}/${occurrenceId}?date=${moment(registeredAt).format('YYYY-MM-DD')}`;
      } else {
        console.warn('no permissions to daily care log occurrences');
        occurrencePath = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Home}`;
      }
    } else {
      if (hadReadPermission(RolePermissionType.Occurrences)) {
        occurrencePath = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Occurrences}?occurrenceId=${occurrenceId}&occurrenceDate=${moment(registeredAt).format('YYYY-MM-DD')}`;
      } else {
        console.warn('no permissions to office occurrences');
        occurrencePath = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}`;
      }
    }
  }

  return occurrencePath;
}
